import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, IconButton, Typography, Switch, CircularProgress, Box, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText, Divider } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, InfoOutlined, Close } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../application/store/useStore';
import { createUserAsync, deleteUserAsync, disableUserAsync, getUsersAsync } from '../../../application/features/users/userThunk';
import { getCompaniesAsync } from '../../../application/features/company/companyThunk';
import { useTranslation } from 'react-i18next';
import { CustomGrid } from '../../../ui/components/common/dataGrid';
import { AddUser } from 'react-iconly';
import { AddUserDialog } from './new-user-dialog';
import { EditUserDialog } from './edit-user-dialog';
import { DeleteUserDialog } from './delete-user-dialog';
import { toast } from 'react-toastify';
import { useCompanyContext } from '../../../application/context/CompanyContext';
const Users = () => {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [currentUserCompanyIds, setCurrentUserCompanyIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const { selectedCompanyId } = useCompanyContext();
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [newUserDialog, setnewUserDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
    const [companiesToShow, setCompaniesToShow] = useState([]);
    const dispatch = useAppDispatch();
    const currentUser = useAppSelector((state) => state.auth.currentUser);
    const formatRoleDisplay = (role) => {
        switch (role) {
            case 'SuperAdmin':
                return 'Super Admin';
            case 'Admin':
                return 'Admin';
            case 'User':
                return 'User';
            default:
                return role;
        }
    };
    const canModifyUser = (targetUserId) => {
        if (currentUser?.id === targetUserId) {
            return false;
        }
        return true;
    };
    const convertRoleEnumToString = (role) => {
        if (typeof role === 'string') {
            return role;
        }
        switch (role) {
            case 0:
                return 'SuperAdmin';
            case 1:
                return 'Admin';
            case 2:
                return 'User';
            default:
                return 'User';
        }
    };
    const handleEditUser = (updatedUser) => {
        setOpenEditDialog(false);
        const companyId = selectedCompanyId === 'ALL' || !selectedCompanyId ? undefined : selectedCompanyId;
        dispatch(getUsersAsync(companyId))
            .unwrap()
            .then((response) => {
            setUsers(response);
        })
            .catch((error) => {
            console.error('Failed to refresh users list:', error);
        });
    };
    useEffect(() => {
        if (currentUser?.role === 'Admin' && currentUserCompanyIds.length === 0) {
            dispatch(getUsersAsync(undefined))
                .unwrap()
                .then((allUsersResponse) => {
                const currentUserDetails = allUsersResponse.find((u) => u.email === currentUser.email);
                if (currentUserDetails?.companyIds) {
                    setCurrentUserCompanyIds(currentUserDetails.companyIds);
                }
            })
                .catch((error) => {
                console.error('Failed to fetch current user details:', error);
            });
        }
    }, [dispatch, currentUser?.role, currentUser?.email, currentUserCompanyIds.length]);
    useEffect(() => {
        dispatch(getCompaniesAsync())
            .unwrap()
            .then((companiesResponse) => {
            if (currentUser?.role === 'SuperAdmin') {
                setCompanies(companiesResponse);
            }
            else if (currentUser?.role === 'Admin') {
                if (currentUserCompanyIds.length > 0) {
                    const userCompanies = companiesResponse.filter((company) => currentUserCompanyIds.includes(company.id));
                    setCompanies(userCompanies);
                }
                else {
                    setCompanies([]);
                }
            }
            else {
                setCompanies([]);
            }
        })
            .catch((error) => {
            console.error('Failed to fetch companies:', error);
        });
    }, [dispatch, currentUser?.role, currentUserCompanyIds]);
    // fetch users according to the company id
    useEffect(() => {
        setIsLoading(true);
        const companyId = selectedCompanyId === 'ALL' || !selectedCompanyId ? undefined : selectedCompanyId;
        dispatch(getUsersAsync(companyId))
            .unwrap()
            .then((usersResponse) => {
            setUsers(usersResponse);
        })
            .catch((error) => {
            console.error('Failed to fetch users:', error);
        })
            .finally(() => setIsLoading(false));
    }, [dispatch, selectedCompanyId]);
    const toggleActivation = (obj) => {
        if (!canModifyUser(obj.id)) {
            toast.error('You cannot disable your own account');
            return;
        }
        dispatch(disableUserAsync(obj)).then(() => {
            setUsers((prevUsers) => prevUsers.map((user) => (user.id === obj.id ? { ...user, isActive: !user.isActive } : user)));
        });
    };
    const handleDeleteUser = () => {
        if (!userToDelete)
            return;
        if (!canModifyUser(userToDelete.id)) {
            toast.error('You cannot delete your own account');
            setIsDeleteDialogOpen(false);
            return;
        }
        dispatch(deleteUserAsync(userToDelete.id))
            .unwrap()
            .then(() => {
            setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userToDelete.id));
            toast.success('User deleted successfully');
        })
            .catch((error) => {
            console.error('Failed to delete user:', error);
            toast.error('Failed to delete user');
        })
            .finally(() => {
            setIsDeleteDialogOpen(false);
            setUserToDelete(null);
        });
    };
    const handleAddUser = (newUser) => {
        newUser.id = undefined;
        const userWithStringRole = {
            ...newUser,
            role: convertRoleEnumToString(newUser.role)
        };
        dispatch(createUserAsync(userWithStringRole))
            .unwrap()
            .then(() => {
            setnewUserDialog(false);
            toast.success(t('userCreated'));
            dispatch(getUsersAsync())
                .unwrap()
                .then((response) => {
                setUsers(response);
            })
                .catch((error) => {
                console.error('Failed to refresh users list:', error);
            });
        })
            .catch((error) => {
            console.error('Failed to add user:', error);
            const errorMessage = error?.error || '';
            if (errorMessage.toLowerCase().includes('already exists')) {
                toast.error(t('userAlreadyExists'));
            }
            else {
                toast.error(t('userCreationFailed'));
            }
        });
    };
    const openCompanyDialog = (companies) => {
        setCompaniesToShow((companies || []).map((c) => ({ id: c.id, name: c.name })));
        setCompanyDialogOpen(true);
    };
    const columns = [
        { field: 'firstName', headerName: t('firstName'), flex: 1, minWidth: 200 },
        { field: 'lastName', headerName: t('lastName'), flex: 1, minWidth: 200 },
        { field: 'email', headerName: t('email'), flex: 1, minWidth: 250 },
        {
            field: 'role',
            headerName: t('role'),
            flex: 1,
            minWidth: 120,
            renderCell: (params) => formatRoleDisplay(String(params.row.role))
        },
        {
            field: 'companyNames',
            headerName: t('company'),
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, maxWidth: '100%', overflow: 'hidden', height: '100%', children: [_jsx(Typography, { variant: 'body2', noWrap: true, title: params.row.companies && params.row.companies.length > 0
                            ? params.row.companies.map((c) => c.name).filter(Boolean).join(', ')
                            : t('noCompany') || 'No Company', children: params.row.companyNames }), params.row.companies && params.row.companies.length > 1 && (_jsx(IconButton, { size: 'small', onClick: () => openCompanyDialog(params.row.companies), sx: { transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.1)' } }, children: _jsx(InfoOutlined, { fontSize: 'small' }) }))] }))
        },
        { field: 'phoneNo', headerName: t('phoneNumber'), flex: 1, minWidth: 180 },
        {
            field: 'status',
            headerName: t('status'),
            flex: 1,
            minWidth: 130,
            renderCell: (params) => (_jsx(Switch, { checked: params.row.isActive, disabled: !canModifyUser(params.row.id), onChange: () => toggleActivation({ id: params.row.id, activeStatus: !params.row.isActive }) }))
        },
        {
            field: 'actions',
            headerName: t('actions'),
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (_jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [_jsx(IconButton, { color: 'primary', onClick: () => {
                            setSelectedUser(params.row);
                            setOpenEditDialog(true);
                        }, children: _jsx(EditIcon, {}) }), _jsx(IconButton, { color: 'error', disabled: !canModifyUser(params.row.id), onClick: () => {
                            setUserToDelete({
                                id: params.row.id,
                                name: `${params.row.firstName} ${params.row.lastName}`
                            });
                            setIsDeleteDialogOpen(true);
                        }, children: _jsx(DeleteIcon, {}) })] }))
        }
    ];
    const filteredUsers = users.filter((user) => {
        if (!searchTerm.trim())
            return true;
        const searchLower = searchTerm.toLowerCase();
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        return (fullName.includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower) ||
            user.phoneNo?.toLowerCase().includes(searchLower) ||
            user.companies?.some((c) => c.name.toLowerCase().includes(searchLower)));
    });
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, paddingY: 1, children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, children: [_jsx(Typography, { variant: 'h3', children: t('users') }), _jsx(Button, { variant: 'contained', startIcon: _jsx(AddUser, {}), color: 'primary', size: 'large', onClick: () => setnewUserDialog(true), children: t('newUser') })] }), _jsx(Box, { display: 'flex', justifyContent: 'flex-start', children: _jsx(TextField, { placeholder: t('searchUsers') || 'Search by name, email, phone, or company...', value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), sx: { width: { xs: '100%', sm: 400, md: 450 }, maxWidth: '100%' }, size: 'small', InputProps: {
                                startAdornment: (_jsx(InputAdornment, { position: 'start', children: _jsx(SearchIcon, {}) }))
                            } }) }), searchTerm.trim() && (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: filteredUsers.length > 0
                            ? `Found ${filteredUsers.length} user${filteredUsers.length === 1 ? '' : 's'} matching "${searchTerm}"`
                            : `No users found matching "${searchTerm}"` })), isLoading ? (_jsx(Box, { display: 'flex', justifyContent: 'center', p: 4, children: _jsx(CircularProgress, {}) })) : (_jsx(Box, { children: filteredUsers.length === 0 && searchTerm.trim() ? (_jsxs(Box, { textAlign: 'center', py: 8, color: 'text.secondary', children: [_jsx(Typography, { variant: 'h6', gutterBottom: true, children: t('noUsersFound') || 'No users found' }), _jsx(Typography, { variant: 'body2', children: t('tryAdjustingSearch') || 'Try adjusting your search criteria' })] })) : (_jsx(CustomGrid, { rows: filteredUsers.map((u) => ({
                                id: u.id,
                                firstName: u.firstName,
                                lastName: u.lastName,
                                email: u.email,
                                phoneNo: u.phoneNo,
                                role: u.role,
                                isActive: u.isActive,
                                companyIds: u.companyIds,
                                companies: u.companies,
                                companyNames: u.companies && u.companies.length > 0
                                    ? u.companies.map((c) => c.name).filter(Boolean).join(', ')
                                    : t('noCompany') || 'No Company'
                            })), columns: columns, sortModel: [] })) }))] }), _jsxs(Dialog, { open: companyDialogOpen, onClose: () => setCompanyDialogOpen(false), fullWidth: true, maxWidth: 'sm', children: [_jsx(DialogTitle, { children: _jsxs(Box, { display: 'flex', alignItems: 'center', justifyContent: 'space-between', children: [_jsx(Typography, { variant: 'h6', children: t('companies') || 'Companies' }), _jsx(IconButton, { onClick: () => setCompanyDialogOpen(false), size: 'small', children: _jsx(Close, {}) })] }) }), _jsx(Divider, {}), _jsx(DialogContent, { dividers: true, children: companiesToShow.length === 0 ? (_jsx(Typography, { variant: 'body2', children: t('noCompany') || 'No Company' })) : (_jsx(_Fragment, { children: _jsx(List, { dense: true, children: companiesToShow.map((company) => (_jsx(ListItem, { children: _jsx(ListItemText, { primary: company.name }) }, company.id))) }) })) })] }), _jsx(AddUserDialog, { openAddUserDialog: newUserDialog, onCloseAddUser: () => setnewUserDialog(false), handleAddUser: handleAddUser, currentUserRole: currentUser?.role, companies: companies }), _jsx(DeleteUserDialog, { onDeleteUser: handleDeleteUser, openDeleteUserDialog: isDeleteDialogOpen, onCloseDeleteUser: () => setIsDeleteDialogOpen(false), userToDelete: userToDelete }), _jsx(EditUserDialog, { onEditUser: handleEditUser, userToEdit: selectedUser, openEditUserDialog: openEditDialog, onCloseEditUser: () => setOpenEditDialog(false), currentUserRole: currentUser?.role, companies: companies })] }));
};
export default Users;
